// Settings → Emergency Numbers. Platforms in scope, the hybrid exception list,
// the unattended auto-fix switch and the digest recipients. The app itself
// (/emergency-numbers) only runs scans and shows the results.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, ShieldAlert, Siren } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import E911BandwidthCard from "./E911BandwidthCard";

const PLATFORMS = [["core", "Core", "uc_platform_core"], ["sas", "SAS", "uc_platform_sas"]];
const lines = (arr) => (arr || []).join("\n");
const parse = (text) => String(text || "").split(/[\n,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);

export default function E911Settings() {
  const [cfg, setCfg] = useState(null);
  const [conns, setConns] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: s }, { data: o }] = await Promise.all([api.get("/e911/settings"), api.get("/e911/overview")]);
    setCfg({ ...s, hybrid_text: lines(s.hybrid_domains), sandbox_text: lines(s.sandbox_patterns),
             recipients_text: (s.alert_recipients || []).join(", ") });
    setConns(o.connections || {});
  }, []);

  useEffect(() => { load().catch(() => toast.error("Could not load the Emergency Numbers settings")); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        platforms: cfg.platforms, hybrid_domains: parse(cfg.hybrid_text), sandbox_patterns: parse(cfg.sandbox_text),
        auto_fix_enabled: !!cfg.auto_fix_enabled, auto_fix_include_ui_only: !!cfg.auto_fix_include_ui_only,
        incremental_hours: Number(cfg.incremental_hours) || 3, max_domains_per_run: Number(cfg.max_domains_per_run) || 0,
        approval_max_age_hours: Number(cfg.approval_max_age_hours) || 24, alerts_enabled: !!cfg.alerts_enabled,
        alert_recipients: parse(cfg.recipients_text).filter((r) => r.includes("@")),
        bandwidth_static_account: String(cfg.bandwidth_static_account || "").replace(/\D/g, ""),
        bandwidth_dlr_account: String(cfg.bandwidth_dlr_account || "").replace(/\D/g, ""),
      };
      const { data } = await api.put("/e911/settings", body);
      setCfg({ ...data, hybrid_text: lines(data.hybrid_domains), sandbox_text: lines(data.sandbox_patterns),
               recipients_text: (data.alert_recipients || []).join(", ") });
      toast.success("Emergency Numbers settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return <Card className="px-4 py-6 text-sm text-slate-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…</Card>;
  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.value }));
  const flip = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));
  const togglePlatform = (p, on) => setCfg((c) => ({ ...c, platforms: on ? [...new Set([...(c.platforms || []), p])] : (c.platforms || []).filter((x) => x !== p) }));

  return (
    <div className="space-y-6" data-testid="settings-e911">
      <div>
        <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-800"><Siren className="h-5 w-5 text-rose-600" /> Emergency Numbers (911)</h3>
        <p className="mb-3 max-w-3xl text-sm text-slate-500">
          The cron assesses every domain's 911 setup against Bandwidth and alerts on unregistered numbers. It never changes
          anything unless the auto-fix switch below is on — and even then only blocker-free plans; a domain with a SIP trunk
          <em> and</em> regular users always waits for a manager's decision in the app.
        </p>
      </div>

      <Card className="space-y-4 px-4 py-4">
        <div>
          <Label className="text-xs text-slate-500">Platforms in scope</Label>
          <div className="mt-1 flex flex-wrap gap-4" data-testid="e911-settings-platforms">
            {PLATFORMS.map(([p, label, alias]) => {
              const ok = conns[alias]?.configured;
              return (
                <label key={p} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" className="h-4 w-4" checked={(cfg.platforms || []).includes(p)}
                         onChange={(e) => togglePlatform(p, e.target.checked)} data-testid={`e911-settings-platform-${p}`} />
                  UC Platform · {label}
                  <span className={`text-[11px] ${ok ? "text-emerald-700" : "text-amber-700"}`}>{ok ? "connected" : "not connected"}</span>
                </label>
              );
            })}
            <Link to="/settings?tab=api-connections" className="text-xs font-medium text-primary hover:underline" data-testid="e911-settings-connections-link">
              API Connections →
            </Link>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">
            Bandwidth: {conns.bandwidth_emergency?.configured ? "credential connected" : "no credential — static registrations read as unknown"}
            {" · "}static account {conns.bandwidth_static?.configured ? conns.bandwidth_static.account : "not set"}
            {" · "}DLR account {conns.bandwidth_dlr?.configured ? conns.bandwidth_dlr.account : "not set — addresses fall back to the Portal's endpoint record"}.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs text-slate-500">Hybrid exceptions (one per line — run Legacy and DLR together on purpose, never planned)</Label>
            <Textarea rows={3} value={cfg.hybrid_text} onChange={set("hybrid_text")} className="font-mono text-[12px]" data-testid="e911-settings-hybrid" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Sandbox patterns (left out of sweeps and the numbers report)</Label>
            <Textarea rows={3} value={cfg.sandbox_text} onChange={set("sandbox_text")} className="font-mono text-[12px]" data-testid="e911-settings-sandbox" />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label className="text-xs text-slate-500">Incremental window (hours)</Label>
            <Input type="number" min={1} value={cfg.incremental_hours ?? 3} onChange={set("incremental_hours")} data-testid="e911-settings-incremental" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Max domains per run (0 = no cap)</Label>
            <Input type="number" min={0} value={cfg.max_domains_per_run ?? 0} onChange={set("max_domains_per_run")} data-testid="e911-settings-cap" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">An approval expires after (hours)</Label>
            <Input type="number" min={1} value={cfg.approval_max_age_hours ?? 24} onChange={set("approval_max_age_hours")} data-testid="e911-settings-approval-age" />
          </div>
        </div>
      </Card>

      <E911BandwidthCard cfg={cfg} set={set} />

      <Card className={`space-y-3 px-4 py-4 ${cfg.auto_fix_enabled ? "border-rose-300 bg-rose-50/40" : ""}`} data-testid="e911-settings-autofix-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <ShieldAlert className={`h-4 w-4 ${cfg.auto_fix_enabled ? "text-rose-600" : "text-slate-400"}`} /> Unattended auto-fix
            </div>
            <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-slate-500">
              When on, the 3-hourly and nightly crons apply plans that have <strong>real 911 work and not a single blocker</strong>
              (for example a SIP trunk domain with no regular users still carrying DLR records → moved to Static). Blocked domains,
              hybrids and trunk + users domains are never touched. Every step is logged under Runs and errors are emailed to the
              recipients below.
            </p>
          </div>
          <Switch checked={!!cfg.auto_fix_enabled} onCheckedChange={flip("auto_fix_enabled")} data-testid="e911-settings-autofix" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" className="h-4 w-4" checked={!!cfg.auto_fix_include_ui_only} disabled={!cfg.auto_fix_enabled}
                 onChange={(e) => flip("auto_fix_include_ui_only")(e.target.checked)} data-testid="e911-settings-autofix-ui" />
          Also fix domains that only need a Portal UI config change
        </label>
      </Card>

      <Card className="space-y-3 px-4 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-slate-800">Unregistered-numbers digest</div>
            <p className="mt-1 text-[12px] text-slate-500">
              Daily at 07:00 (Edmonton): every emergency number the Portal knows that Bandwidth holds nothing for, plus numbers
              registered on the wrong side. Run problems from a scan or fix go to the same people.
            </p>
          </div>
          <Switch checked={cfg.alerts_enabled !== false} onCheckedChange={flip("alerts_enabled")} data-testid="e911-settings-alerts" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Send to (comma separated)</Label>
          <Input value={cfg.recipients_text} onChange={set("recipients_text")} placeholder="noc@unitedcloud.ca" data-testid="e911-settings-recipients" />
        </div>
      </Card>

      <div className="flex items-center justify-end gap-3 border-t pt-3">
        <Link to="/emergency-numbers" className="text-xs font-medium text-primary hover:underline" data-testid="e911-settings-open-app">Open Emergency Numbers →</Link>
        <Button size="sm" onClick={save} disabled={busy} data-testid="e911-settings-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </Button>
      </div>
    </div>
  );
}
